import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import imageCompression from "browser-image-compression";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { trpcClient } from "@/clients/trpcClient";
import { extractExifLocation, haversineDistance, offsetPosition } from "@/lib/utils/exif";
import { blurFaces } from "@/lib/utils/faceBlur";
import { fileToBase64 } from "@/lib/utils/fileUtils";
import { getBrowserLocation } from "@/lib/utils/geolocation";
import { trpc } from "@/router";

// ─── Types ──────────────────────────────────────────────────────────────────

/** Top-level feature categories shown as cards */

export const FEATURE_CATEGORIES = [{
  id: "entre",
  label: "Entré",
  description: "Dörrar, trappsteg, ramper",
  icon: "door-open",
  typeNames: ["Huvudentré", "Sidoentré", "Nödutgång"],
  pinRequired: true,
  photoTips: ["Visa hela dörren, tröskeln och markytan framför", "Fotografera rakt framifrån i dagsljus", "Undvik personer och registreringsskyltar"]
}, {
  id: "parkering",
  label: "Parkering",
  description: "Handikapp-p, kantsten, underlag",
  icon: "car",
  typeNames: ["Parkering"],
  pinRequired: true,
  photoTips: ["Stå på rutan när du tar bilden — GPS blir rätt", "Visa eventuell skylt och markmålning", "Få gärna med entrén i bakgrunden"]
}, {
  id: "toalett",
  label: "Toalett",
  description: "Handikapptoalett, utrymme, stöd",
  icon: "accessibility",
  typeNames: ["Toalett"],
  pinRequired: false,
  photoTips: ["Visa toaletten, dörrbredden och eventuella stöd", "Fotografera i dagsljus", "Undvik personer och registreringsskyltar"]
}, {
  id: "hiss",
  label: "Hiss",
  description: "Storlek, knappar, tillgänglighet",
  icon: "arrow-up-down",
  typeNames: ["Hiss"],
  pinRequired: false,
  photoTips: ["Visa hissdörren och panelen med knappar", "Fotografera inifrån om möjligt", "Undvik personer och registreringsskyltar"]
}, {
  id: "annat",
  label: "Annat",
  description: "Service, personalentré",
  icon: "more-horizontal",
  typeNames: ["Service"],
  pinRequired: false,
  photoTips: ["Visa det du vill rapportera tydligt", "Fotografera i dagsljus", "Undvik personer och registreringsskyltar"]
}];

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useBidra() {
  const {
    plats,
    platsNamn
  } = useSearch({
    from: "/_app/bidra"
  });
  const navigate = useNavigate();

  // Core state
  const [step, setStep] = useState(plats ? "feature-type" : "place");
  const [placeId, setPlaceId] = useState(plats ?? null);
  const [placeName, setPlaceName] = useState(platsNamn ?? null);
  const [error, setError] = useState(null);
  const [featureCategory, setFeatureCategory] = useState(null);

  // New place state
  const [newPlaceName, setNewPlaceName] = useState("");
  const [newPlaceCategoryId, setNewPlaceCategoryId] = useState(null);
  const [pinLat, setPinLat] = useState();
  const [pinLng, setPinLng] = useState();

  // Photo + feature state
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [featureTypeId, setFeatureTypeId] = useState(null);
  const [extraInfo, setExtraInfo] = useState("");
  const [showExtraInfo, setShowExtraInfo] = useState(false);

  // Feature location
  const [featureLat, setFeatureLat] = useState();
  const [featureLng, setFeatureLng] = useState();

  // Place location (for constraining pin map + validating EXIF distance)
  const {
    data: placeData
  } = useQuery({
    ...trpc.places.byId.queryOptions({
      id: placeId
    }),
    enabled: !!placeId
  });
  const placeLocation = placeData ? {
    latitude: placeData.location.latitude,
    longitude: placeData.location.longitude
  } : pinLat && pinLng ? {
    latitude: pinLat,
    longitude: pinLng
  } : null;

  // Derived: current category config
  const categoryConfig = featureCategory ? FEATURE_CATEGORIES.find(c => c.id === featureCategory) : null;

  // Queries
  const {
    data: allFeatureTypes = []
  } = useQuery(trpc.features.types.queryOptions());
  // Filter feature types to current category
  const featureTypes = categoryConfig ? allFeatureTypes.filter(t => categoryConfig.typeNames.includes(t.name)) : [];

  // Auto-select handled in selectCategory below

  const {
    data: allCategories
  } = useQuery(trpc.experiences.categories.all.queryOptions());
  const parentCategories = (allCategories ?? []).filter(c_0 => c_0.parentId === null);
  const childCategories = (allCategories ?? []).filter(c_1 => c_1.parentId !== null);

  // Submit
  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPhoto || !featureTypeId) {
        throw new Error("Fyll i alla obligatoriska fält");
      }
      const isNewPlace = !placeId;
      if (isNewPlace && (!newPlaceName.trim() || !newPlaceCategoryId)) {
        throw new Error("Fyll i namn och kategori för platsen");
      }
      if (isNewPlace && (!pinLat || !pinLng)) {
        throw new Error("Välj plats på kartan");
      }

      // Blur faces before upload (privacy, uses FaceDetector API where available)
      const blurred = await blurFaces(selectedPhoto);
      const compressed = await imageCompression(blurred, {
        maxSizeMB: 1.5,
        maxWidthOrHeight: 1920,
        fileType: "image/webp",
        useWebWorker: true
      });
      const base64 = await fileToBase64(compressed);
      const featureTypeName = allFeatureTypes.find(t_0 => t_0.id === featureTypeId)?.name;
      const [uploadResult, aiResult] = await Promise.all([trpcClient.uploads.upload.mutate({
        fileData: base64,
        fileName: `feature-${Date.now()}.webp`,
        contentType: "image/webp",
        bucket: "feature-photos"
      }), trpcClient.features.assess.mutate({
        image: base64,
        featureType: featureTypeName
      }).then(r => ({
        data: r.assessment,
        confidence: r.assessment.confidence
      })).catch(() => null)]);
      await trpcClient.features.create.mutate({
        placeId: placeId ?? undefined,
        newPlace: isNewPlace ? {
          name: newPlaceName.trim(),
          categoryId: newPlaceCategoryId
        } : undefined,
        featureTypeId,
        location: featureLat && featureLng ? {
          latitude: featureLat,
          longitude: featureLng
        } : {
          latitude: 0,
          longitude: 0
        },
        photo: {
          url: uploadResult.url
        },
        description: extraInfo || undefined,
        assessment: aiResult ?? undefined
      });
    },
    onSuccess: () => {
      toast.success("Tack för ditt bidrag!", {
        description: "Din rapport granskas och publiceras snart.",
        duration: 6000
      });
      setStep("done");
    },
    onError: err => {
      toast.error("Något gick fel", {
        description: err instanceof Error ? err.message : "Försök igen"
      });
      setStep("photo");
    }
  });
  const handleSubmit = () => {
    setError(null);
    setStep("submitting");
    submitMutation.mutate();
  };
  const handleBack = () => {
    if (step === "pin-feature") {
      setStep("photo");
    } else if (step === "photo") {
      setStep("feature-type");
    } else if (step === "feature-type" && !plats) {
      setStep(placeId ? "place" : "new-place");
    } else if (step === "feature-type" && plats) {
      navigate({
        to: "/karta"
      });
    } else if (step === "new-place") {
      setStep("place");
    } else {
      navigate({
        to: "/karta"
      });
    }
  };
  const handlePhotoContinue = async () => {
    if (!selectedPhoto) return;
    try {
      const exif = await extractExifLocation(selectedPhoto);
      if (exif && placeLocation) {
        const dist = haversineDistance(placeLocation.latitude, placeLocation.longitude, exif.latitude, exif.longitude);
        // Only use EXIF if within 500m of the place
        if (dist < 500) {
          const offset = offsetPosition(exif.latitude, exif.longitude, exif.bearing, 3);
          setFeatureLat(offset.latitude);
          setFeatureLng(offset.longitude);
        } else {
          // EXIF too far — center on place instead
          setFeatureLat(placeLocation.latitude);
          setFeatureLng(placeLocation.longitude);
        }
      } else if (exif) {
        const offset_0 = offsetPosition(exif.latitude, exif.longitude, exif.bearing, 3);
        setFeatureLat(offset_0.latitude);
        setFeatureLng(offset_0.longitude);
      } else if (placeLocation) {
        setFeatureLat(placeLocation.latitude);
        setFeatureLng(placeLocation.longitude);
      } else {
        const gps = await getBrowserLocation(5000);
        if (gps) {
          setFeatureLat(gps.latitude);
          setFeatureLng(gps.longitude);
        }
      }
    } catch {
      // EXIF extraction failed — center on place if available
      if (placeLocation) {
        setFeatureLat(placeLocation.latitude);
        setFeatureLng(placeLocation.longitude);
      }
    }
    setStep("pin-feature");
  };
  const handleReset = useCallback(() => {
    setStep(plats ? "feature-type" : "place");
    setFeatureCategory(null);
    setPlaceId(plats ?? null);
    setPlaceName(platsNamn ?? null);
    setNewPlaceName("");
    setNewPlaceCategoryId(null);
    setPinLat(undefined);
    setPinLng(undefined);
    setSelectedPhoto(null);
    setFeatureTypeId(null);
    setExtraInfo("");
    setShowExtraInfo(false);
    setFeatureLat(undefined);
    setFeatureLng(undefined);
    setError(null);
  }, [plats, platsNamn]);
  const navigateToKarta = () => navigate({
    to: "/karta"
  });
  return {
    step,
    error,
    // Feature category
    featureCategory,
    categoryConfig,
    selectCategory: cat => {
      setFeatureCategory(cat);
      const config = FEATURE_CATEGORIES.find(c_2 => c_2.id === cat);
      const types = config ? allFeatureTypes.filter(t_1 => config.typeNames.includes(t_1.name)) : [];
      setFeatureTypeId(types[0]?.id ?? null);
      setStep("photo");
    },
    // Place
    placeId,
    placeName,
    selectPlace: (id, name) => {
      setPlaceId(id);
      setPlaceName(name);
      setStep("feature-type");
    },
    goToNewPlace: () => setStep("new-place"),
    // New place
    newPlaceName,
    setNewPlaceName,
    newPlaceCategoryId,
    setNewPlaceCategoryId,
    pinLat,
    pinLng,
    setPinLocation: (lat, lng) => {
      setPinLat(lat);
      setPinLng(lng);
    },
    isNewPlaceValid: !!newPlaceName.trim() && !!newPlaceCategoryId && !!pinLat && !!pinLng,
    continueFromNewPlace: () => setStep("feature-type"),
    parentCategories,
    childCategories,
    // Photo
    selectedPhoto,
    setSelectedPhoto,
    featureTypeId,
    setFeatureTypeId,
    featureTypes,
    extraInfo,
    setExtraInfo,
    showExtraInfo,
    setShowExtraInfo: () => setShowExtraInfo(true),
    isPhotoValid: selectedPhoto !== null && featureTypeId !== null,
    // Feature pin
    featureLat,
    featureLng,
    setFeatureLocation: (lat_0, lng_0) => {
      setFeatureLat(lat_0);
      setFeatureLng(lng_0);
    },
    isFeatureLocationValid: !!featureLat && !!featureLng,
    // Actions
    handleSubmit,
    handleBack,
    handlePhotoContinue,
    handleReset,
    navigateToKarta,
    // Place location for pin map context
    placeLocation
  };
}